import type { MyGroupUsageResponse } from "../../api/organisationReporting";

/**
 * How the My Groups panels are ordered. `hierarchy` is the order the server lists them in (divisions,
 * then their departments and groups); the others sort by the figure named, with names A to Z and the
 * figures largest first. A group with no figure for the key sorts after those that have one.
 */
export type GroupSortKey = "hierarchy" | "name" | "spend" | "people" | "perPerson" | "projected";

export const groupSortKeys: readonly GroupSortKey[] = ["hierarchy", "name", "spend", "people", "perPerson", "projected"];

/**
 * Whether a group's panel is shown: a group with nobody in it this period never is, and when the viewer
 * hides empty groups, an entitlement group with no direct members is not either. Divisions and departments
 * have no memberships, so they are never hidden as empty.
 *
 * @param group - The group.
 * @param hideEmptyGroups - Whether the viewer has chosen to hide empty groups.
 */
export function isShownGroup(group: MyGroupUsageResponse, hideEmptyGroups: boolean): boolean {
  if (group.personCount === 0) {
    return false;
  }

  return !(hideEmptyGroups && group.kind === "Group" && group.directMemberCount === 0);
}

type SortValue = string | number | null;

/**
 * The groups in the requested order. Returns a new array; `hierarchy` returns the groups as given.
 *
 * @param groups - The groups as the server lists them.
 * @param sortKey - The figure to order by.
 * @param projectedTotalFor - A group's projected spend for the period, or `null` when there is none.
 */
export function sortGroups(
  groups: readonly MyGroupUsageResponse[],
  sortKey: GroupSortKey,
  projectedTotalFor: (groupId: string) => number | null,
): MyGroupUsageResponse[] {
  if (sortKey === "hierarchy") {
    return [...groups];
  }

  return [...groups].sort((a, b) =>
    compareValues(valueFor(a, sortKey, projectedTotalFor), valueFor(b, sortKey, projectedTotalFor), sortKey !== "name"),
  );
}

function valueFor(
  group: MyGroupUsageResponse,
  sortKey: GroupSortKey,
  projectedTotalFor: (groupId: string) => number | null,
): SortValue {
  switch (sortKey) {
    case "name":
      return group.groupLabel;
    case "spend":
      return group.totalSpend;
    case "people":
      return group.personCount;
    case "perPerson":
      // Spend per person finds the groups whose individuals spend the most; with nobody in it there is no average.
      return group.totalSpend !== null && group.personCount > 0 ? group.totalSpend / group.personCount : null;
    case "projected":
      return projectedTotalFor(group.groupId);
    case "hierarchy":
      return null;
  }
}

function compareValues(a: SortValue, b: SortValue, descending: boolean): number {
  if (a === null || b === null) {
    if (a === b) {
      return 0;
    }

    // Missing figures always go last, whichever way the rest is sorted.
    return a === null ? 1 : -1;
  }

  if (typeof a === "string" && typeof b === "string") {
    return a.localeCompare(b);
  }

  if (typeof a === "number" && typeof b === "number") {
    return descending ? b - a : a - b;
  }

  return 0;
}
